'use memo';

import { cleanFullAddress, PropertyAddress } from '@urbanx/agx-ui-components';
import {
  AutopilotTaskDto,
  TaskCategoryStage,
  TaskCategoryWorkflowType,
} from '@urbanx/agx.activities.client';
import { AuthContext } from '@urbanx/agx.auth.client';
import { MenuTitles } from 'constants/menu';
import { useSelectedAgency, useSelectedCampaign } from 'hooks';
import { ContentLayout, PageLayout } from 'layout';
import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';
import Tasks, { TaskTabs } from 'components/tasks/Tasks';
import AgentExchange from './ExchangeTasks/AgentExchange';
import ContractExchange from './ExchangeTasks/ContractExchange';
import SolicitorExchange from './ExchangeTasks/SolicitorExchange';
import ContractCompliance from './ContractCompliance/ContractCompliance';
import TerminateContractAction from './TerminateContractAction';
import { getStepsCompletionInfo } from 'utils/tasks';
import {
  ExchangeTaskGroup,
  getExchangeTaskGroups,
  isExchangeTaskType,
} from './ExchangeTasks/ExchangeTask.shared';

const isUnlockedExchangeTask = (task: AutopilotTaskDto) =>
  task.isLocked === false && isExchangeTaskType(task.category?.taskType);

const ContractTasks = () => {
  const { contractId } = useParams();
  const { userAccount } = useContext(AuthContext);
  const { agency } = useSelectedAgency();
  const { campaign } = useSelectedCampaign();
  const [allTasks, setAllTasks] = useState<AutopilotTaskDto[]>([]);

  const handleTasksChange = (tasks: AutopilotTaskDto[]) => {
    setAllTasks(tasks);
  };

  const exchangeTasks = allTasks.filter(isUnlockedExchangeTask);
  const exchangeTaskGroups = getExchangeTaskGroups(exchangeTasks);
  const contractComplianceTasks = allTasks
    .filter(
      (task) =>
        !task.isLocked &&
        task.category?.workflowType === TaskCategoryWorkflowType.Contract &&
        task.category?.stage === TaskCategoryStage.ContractCompliance
    )
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const allExchangeTasksCompleted =
    exchangeTasks.length > 0 && exchangeTasks.every((task) => task.isCompleted === true);
  const { allStepsCompleted: allContractComplianceStepsCompleted } =
    getStepsCompletionInfo(contractComplianceTasks);
  const taskFilter = (task: AutopilotTaskDto) =>
    !isUnlockedExchangeTask(task) && task.category?.stage !== TaskCategoryStage.ContractCompliance;

  const renderExchangeTask = (group: ExchangeTaskGroup) => {
    if (!campaign?.campaignId) return null;

    switch (group.family) {
      case 'solicitor':
        return (
          <SolicitorExchange
            key={`${group.tasks[0]?.contractId}-solicitor-exchange`}
            campaignId={campaign.campaignId}
            group={group}
          />
        );
      case 'agent':
        return (
          <AgentExchange
            key={`${group.tasks[0]?.contractId}-agent-exchange`}
            campaignId={campaign.campaignId}
            group={group}
          />
        );
      case 'contract':
        return (
          <ContractExchange
            key={`${group.tasks[0]?.contractId}-contract-exchange`}
            campaignId={campaign.campaignId}
            group={group}
          />
        );
      default:
        return null;
    }
  };

  const renderAdditionalContent = ({
    selectedTab,
    selectedWorkflowStage,
    allTasksCompleted,
    isViewingCompletedStage,
  }: {
    selectedTab: TaskTabs;
    selectedWorkflowStage?: TaskCategoryStage;
    allTasksCompleted: boolean;
    isViewingCompletedStage: boolean;
  }) => {
    if (selectedTab !== TaskTabs.Tasks) return null;

    if (selectedWorkflowStage === TaskCategoryStage.ContractCompliance) {
      if (contractComplianceTasks.length === 0) return null;
      if (allTasksCompleted && !isViewingCompletedStage) return null;
      if (allContractComplianceStepsCompleted && !isViewingCompletedStage) return null;
      if (!campaign?.campaignId) return null;

      return (
        <ContractCompliance campaignId={campaign.campaignId} tasks={contractComplianceTasks} />
      );
    }

    if (isViewingCompletedStage && selectedWorkflowStage !== TaskCategoryStage.Exchange)
      return null;
    if (allTasksCompleted && !isViewingCompletedStage) return null;
    if (selectedWorkflowStage !== TaskCategoryStage.Exchange) return null;
    if (exchangeTasks.length === 0) return null;
    if (allExchangeTasksCompleted && !isViewingCompletedStage) return null;

    return exchangeTaskGroups.map(renderExchangeTask);
  };

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={`Campaigns / ${cleanFullAddress(campaign?.address as PropertyAddress)}`}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.CONTRACT_TASKS}
        isContracts={true}
        secondaryActions={<TerminateContractAction />}
      >
        <Tasks
          campaignId={campaign?.campaignId ?? ''}
          contractId={contractId}
          primaryWorkflowType={TaskCategoryWorkflowType.Contract}
          taskFilter={taskFilter}
          onTasksChange={handleTasksChange}
          renderAdditionalContent={renderAdditionalContent}
        />
      </ContentLayout>
    </PageLayout>
  );
};

export default ContractTasks;
